import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/hq/useDraft";
import { Upload } from "lucide-react";
import { toast } from "sonner";

const monoCls = "mono rounded-sm bg-[#0A0A0A] text-sm";

/** Paste-a-path or upload-a-file photo control used across every HQ tab. */
export const ImageField = ({
  label = "PHOTO",
  value = "",
  onChange,
  testId,
  placeholder = "Paste a path or upload a photo",
  preview = true,
}) => {
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/images", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      onChange(data.url);
      toast.success(`Uploaded ${data.filename} — remember to save`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const control = (
    <div className="flex flex-wrap items-center gap-2">
      {preview && value ? (
        <img src={value} alt="" className="h-10 w-10 shrink-0 border border-border object-cover" />
      ) : null}
      <Input
        data-testid={testId ? `${testId}-url` : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${monoCls} min-w-[12rem] flex-1`}
      />
      <label
        data-testid={testId ? `${testId}-upload` : undefined}
        className="mono flex cursor-pointer items-center gap-1 whitespace-nowrap border border-border px-3 py-2 text-[10px] tracking-widest text-primary transition-colors hover:border-primary"
      >
        <Upload size={12} />
        {busy ? "UPLOADING…" : "UPLOAD"}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </label>
    </div>
  );

  return label ? <Field label={label}>{control}</Field> : control;
};
